// installCache.js
// ---------------------------------------------------------------------------
// Makes the Glannu store load instantly without waiting on the Render backend.
//
// How it works:
//   * Product + category READS (GET /api/products, /api/products/:id,
//     /api/categories) are served from a STATIC snapshot shipped with the
//     site (public/products.json + public/categories.json) — instant, from
//     Vercel's CDN, no backend needed.
//   * The snapshot is cached in localStorage (browser cache) so repeat visits
//     are instant even before the static files load.
//   * In the background we still try the LIVE backend; if it's awake we quietly
//     refresh the cache so data is up to date. If it's asleep, nobody waits.
//   * All searching / filtering / sorting / pagination is done in the browser
//     (trivial for ~168 products) and mirrors the backend's response shape, so
//     StorePage / ProductPage need NO changes.
//
// Writes (admin add/edit/delete), quotes (RFQ) and auth are NOT touched — they
// go straight to the backend as before.
// ---------------------------------------------------------------------------
import axios from "axios";
import { featuredCompare, brandOf, canonBrand } from "@/lib/catalog";

const SNAP_PRODUCTS = "/products.json";
const SNAP_CATEGORIES = "/categories.json";
const LS_KEY = "glannu_data_cache_v2"; // v2: adds freshness tracking (old v1 copies are ignored)
const RECHECK_MS = 5 * 60 * 1000; // re-check for updates when the user returns after 5+ min
export const DATA_UPDATED_EVENT = "glannu:data-updated";

// mem = { products, categories, sig, liveAt, snapAt }
//   sig    - fingerprint of the product list (detects price/stock/product changes)
//   liveAt - when this data last came from the live backend (ms)
//   snapAt - build time of the CDN snapshot this data came from (ms)
let mem = null;
let loadPromise = null;
let checking = null;
let lastCheck = 0;
let apiBaseSeen = null;

function readLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {}
  return null;
}

function writeLS(data) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch (e) {}
}

async function fetchJSON(url, opts) {
  const res = await fetch(url, Object.assign({ cache: "no-store" }, opts || {}));
  if (!res.ok) throw new Error(url + " -> " + res.status);
  return res.json();
}

function signature(products, categories) {
  let h = 0;
  const add = (s) => { for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; };
  for (const p of products || []) {
    add(`${p.id}|${p.name}|${p.price}|${p.in_stock ? 1 : 0}|${p.deleted ? 1 : 0}|${p.image_url || ""}|${p.category}|${p.moq}`);
  }
  for (const c of categories || []) add(`${c.name}|${c.count}|${c.image_url || ""}`);
  return `${(products || []).length}:${h}`;
}

// Take new data if it differs from what we have; tell the open page to redraw.
function adopt(products, categories, meta) {
  if (!products || !products.length) return false;
  const cats = categories && categories.length ? categories : (mem && mem.categories) || [];
  const sig = signature(products, cats);
  const prev = mem;
  mem = Object.assign({}, prev || {}, meta, { products, categories: cats, sig });
  writeLS(mem);
  if (prev && prev.sig !== sig) {
    try { window.dispatchEvent(new CustomEvent(DATA_UPDATED_EVENT)); } catch (e) {}
  }
  return !prev || prev.sig !== sig;
}

// Two sources, checked in this order:
//   1. CDN snapshot - fast and always up (rebuilt on every deploy + daily)
//   2. live backend - the truth, but can take ~1 min to wake on the free tier
// A snapshot is only used if it was built after our last live copy, so an
// older snapshot can never overwrite fresher live data.
async function refreshSnapshot() {
  try {
    const bust = `?t=${Date.now()}`;
    const [p, c] = await Promise.all([
      fetchJSON(SNAP_PRODUCTS + bust),
      fetchJSON(SNAP_CATEGORIES + bust).catch(() => null),
    ]);
    const snapAt = Date.parse((p && p.generated_at) || "") || 0;
    const products = (p && p.products) || (Array.isArray(p) ? p : []);
    if (!mem || !mem.liveAt || snapAt > mem.liveAt) adopt(products, c, { snapAt });
  } catch (e) { /* snapshot unavailable */ }
}

async function refreshLive(apiBase) {
  if (!apiBase) return;
  try {
    const [p, c] = await Promise.all([
      fetchJSON(`${apiBase}/products?limit=5000`),
      fetchJSON(`${apiBase}/categories`),
    ]);
    adopt((p && p.products) || [], c, { liveAt: Date.now() });
  } catch (e) { /* backend asleep - keep what we have, the next check retries */ }
}

function refresh(apiBase) {
  if (checking) return checking;
  lastCheck = Date.now();
  checking = refreshSnapshot().then(() => refreshLive(apiBase)).finally(() => { checking = null; });
  return checking;
}

// Load once: browser copy (instant) -> snapshot (fast) -> live. Always re-checks in the background.
function ensureLoaded(apiBase) {
  apiBaseSeen = apiBase || apiBaseSeen;
  if (mem) return Promise.resolve(mem);
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    const ls = readLS();
    if (ls && ls.products && ls.products.length) {
      mem = ls;
      refresh(apiBase); // show the saved copy instantly; the screen updates if anything changed
      return mem;
    }
    // First visit: wait only for the fast snapshot, never for a sleeping backend.
    lastCheck = Date.now();
    await refreshSnapshot();
    if (mem) { refreshLive(apiBase); return mem; }
    await refreshLive(apiBase);
    return mem; // may be null -> caller falls back to the real backend
  })();
  return loadPromise;
}

// Phones keep tabs alive for days without reloading: re-check when the user comes back.
function recheckIfStale() {
  if (document.visibilityState === "visible" && mem && Date.now() - lastCheck > RECHECK_MS) {
    refresh(apiBaseSeen);
  }
}
try {
  document.addEventListener("visibilitychange", recheckIfStale);
  window.addEventListener("focus", recheckIfStale);
  window.addEventListener("pageshow", (e) => { if (e.persisted) recheckIfStale(); }); // back/forward cache
  localStorage.removeItem("glannu_data_cache_v1"); // drop the old-format copy
} catch (e) {}

// Replicate the backend's GET /api/products query semantics in the browser.
function applyQuery(all, params) {
  params = params || {};
  let list = (all || []).filter((p) => !p.deleted);

  if (params.category) list = list.filter((p) => p.category === params.category);
  if (params.search) {
    const s = String(params.search).toLowerCase();
    list = list.filter((p) => (p.name || "").toLowerCase().includes(s));
  }
  if (params.in_stock === "true") list = list.filter((p) => p.in_stock === true);
  else if (params.in_stock === "false") list = list.filter((p) => p.in_stock === false);
  if (params.brand) {
    const b = canonBrand(params.brand).toLowerCase();
    list = list.filter((p) => brandOf(p).toLowerCase() === b);
  }

  const sort = params.sort;
  if (sort === "price_asc") list.sort((a, b) => (a.price || 0) - (b.price || 0));
  else if (sort === "price_desc") list.sort((a, b) => (b.price || 0) - (a.price || 0));
  else if (sort === "name_asc") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  else if (sort === "name_desc") list.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
  else if (sort === "featured") {
    // Mirrors backend sort=featured (see lib/catalog.js): hybrid > on-grid > micro > battery > ESS > rest, cheapest first
    list.sort(featuredCompare);
  }
  else list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));

  const total = list.length;
  const page = parseInt(params.page || 1, 10) || 1;
  const limit = parseInt(params.limit || 20, 10) || 20;
  const start = (page - 1) * limit;
  const products = list.slice(start, start + limit);
  return {
    products,
    total,
    page,
    limit,
    total_pages: Math.max(1, Math.ceil(total / limit)),
  };
}

function jsonResponse(payload, config) {
  return {
    data: payload,
    status: 200,
    statusText: "OK",
    headers: {},
    config,
    request: {},
  };
}

const UUID_RE = /\/api\/products\/[0-9a-fA-F-]{36}$/;
const LIST_RE = /\/api\/products(\?|$)/;
const CATS_RE = /\/api\/categories(\?|$)/;

axios.interceptors.request.use(async (config) => {
  try {
    const method = (config.method || "get").toLowerCase();
    const url = config.url || "";
    if (method !== "get") return config;

    const isList = LIST_RE.test(url);
    const isSingle = UUID_RE.test(url);
    const isCats = CATS_RE.test(url);
    if (!isList && !isSingle && !isCats) return config;

    const apiBase = url.replace(/\/api\/.*$/, "/api");
    const data = await ensureLoaded(apiBase);
    if (!data || !data.products || !data.products.length) {
      return config; // nothing cached yet -> let it hit the real backend
    }

    if (isCats) {
      config.adapter = async () => jsonResponse(data.categories || [], config);
    } else if (isSingle) {
      const id = url.split("?")[0].split("/").pop();
      const found = data.products.find((p) => p.id === id);
      if (!found) return config; // unknown id -> let backend handle it
      config.adapter = async () => jsonResponse(found, config);
    } else {
      config.adapter = async () => jsonResponse(applyQuery(data.products, config.params), config);
    }
  } catch (e) {
    /* never break a request because of the cache layer */
  }
  return config;
});

export default true;
